import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MagicWand, ArrowRight, PenNib, CalendarBlank, TrendUp, ClockCounterClockwise,
} from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { PLATFORM_META, StatusBadge } from "@/components/postMeta";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { active, activeId } = useWorkspace();
  const [content, setContent] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/content?workspace_id=${activeId}`).then((r) => setContent(r.data)).catch(() => {});
    api.get(`/posts?workspace_id=${activeId}`).then((r) => setPosts(r.data)).catch(() => {});
  }, [activeId]);

  const scheduled = posts.filter((p) => p.status === "scheduled");
  const usagePct = active ? Math.min(100, Math.round((active.usage_count / active.usage_limit) * 100)) : 0;

  const stats = [
    { label: "Content processed", value: content.length, icon: PenNib },
    { label: "Posts generated", value: posts.length, icon: MagicWand },
    { label: "Scheduled", value: scheduled.length, icon: CalendarBlank },
  ];

  return (
    <AppLayout>
      <div className="mb-8">
        <p className="text-sm font-mono uppercase text-muted-foreground tracking-wide">
          {active?.name || "Workspace"}
        </p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-1">
          Welcome back, {(user?.name || "there").split(" ")[0]}.
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="md:col-span-3 rounded-lg border border-border bg-primary text-primary-foreground p-8 flex flex-col justify-between min-h-[220px] relative overflow-hidden"
        >
          <div className="relative z-10">
            <Badge variant="secondary" className="mb-4 font-mono text-xs">THE HERO ACTION</Badge>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight max-w-md leading-tight">
              Drop in one piece of content. Get a full week of posts.
            </h2>
          </div>
          <Button
            variant="secondary" size="lg" data-testid="dashboard-new-repurpose"
            onClick={() => navigate("/studio")} className="relative z-10 mt-6 w-fit group"
          >
            <MagicWand size={18} weight="fill" className="mr-2" />
            New Repurpose
            <ArrowRight size={18} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Button>
          <MagicWand size={220} weight="fill" className="absolute -right-8 -bottom-12 opacity-10" />
        </motion.div>

        {/* Usage */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="rounded-lg border border-border bg-card p-6 flex flex-col"
          data-testid="dashboard-usage-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium">Usage</span>
            <Badge variant="outline" className="font-mono uppercase text-xs">{active?.plan || "trial"}</Badge>
          </div>
          <div className="flex items-end gap-1 mb-2">
            <span className="font-heading font-black text-4xl">{active?.usage_count ?? 0}</span>
            <span className="text-muted-foreground font-mono mb-1.5">/ {active?.usage_limit ?? 3}</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden mb-3">
            <div className={`h-full rounded-full transition-all ${usagePct >= 90 ? "bg-destructive" : "bg-primary"}`} style={{ width: `${usagePct}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mb-4">Repurposes this month</p>
          <Button variant="outline" size="sm" className="mt-auto" data-testid="dashboard-upgrade" onClick={() => navigate("/billing")}>
            <TrendUp size={16} className="mr-1.5" /> Upgrade plan
          </Button>
        </motion.div>

        {/* Stats */}
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.04 }}
            className="rounded-lg border border-border bg-card p-6 hover:-translate-y-0.5 transition-transform"
          >
            <s.icon size={22} className="text-primary mb-3" />
            <p className="font-heading font-black text-3xl text-foreground">{s.value}</p>
            <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
          </motion.div>
        ))}

        {/* Recent content */}
        <div className="md:col-span-2 rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ClockCounterClockwise size={20} /> Recent content</h3>
            <button className="text-sm text-primary hover:underline" data-testid="view-library" onClick={() => navigate("/library")}>View all</button>
          </div>
          {content.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No content yet. Start your first repurpose →</p>
          ) : (
            <div className="space-y-2">
              {content.slice(0, 5).map((c) => (
                <button
                  key={c.id}
                  data-testid={`recent-content-${c.id}`}
                  onClick={() => navigate(`/studio/${c.id}`)}
                  className="w-full flex items-center gap-3 p-3 rounded-md border border-border hover:bg-accent transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-md bg-secondary flex items-center justify-center shrink-0">
                    <PenNib size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{c.title}</p>
                    <p className="text-xs text-muted-foreground font-mono uppercase">{c.source_type}</p>
                  </div>
                  {c.repurposed && <Badge variant="secondary" className="text-xs">done</Badge>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming */}
        <div className="md:col-span-2 rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><CalendarBlank size={20} /> Upcoming</h3>
            <button className="text-sm text-primary hover:underline" data-testid="view-calendar" onClick={() => navigate("/calendar")}>Calendar</button>
          </div>
          {scheduled.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Nothing scheduled yet.</p>
          ) : (
            <div className="space-y-2">
              {scheduled.slice(0, 5).map((p) => {
                const meta = PLATFORM_META[p.platform];
                return (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-md border border-border">
                    <meta.icon size={18} weight="fill" style={{ color: meta.color }} />
                    <p className="text-sm truncate flex-1">{p.body.slice(0, 60)}…</p>
                    <span className="text-xs font-mono text-muted-foreground shrink-0">
                      {new Date(p.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
